import type {
  PromptOptimizeAcceptPayload,
  PromptOptimizeCompletePayload,
  PromptOptimizeLoadingPayload,
  PromptOptimizeRejectPayload,
  PromptOptimizeRequestPayload,
} from "../types"

export type MetricViewerEventType =
  | "metric:updated"
  | "metric:saved"
  | "metric:saveError"
  | "prompt:optimizeRequest"
  | "prompt:optimizeLoading"
  | "prompt:optimizeComplete"
  | "prompt:optimizeAccept"
  | "prompt:optimizeReject"
  | "prompt:optimizeDismiss"
  | "testMetric:open"
  | "testMetric:run"
  | "testMetric:stop"
  | "testMetric:resultsUpdated"

export interface MetricViewerEventPayloads {
  "metric:updated": undefined
  "metric:saved": undefined
  "metric:saveError": { error: string }
  "prompt:optimizeRequest": PromptOptimizeRequestPayload
  "prompt:optimizeLoading": PromptOptimizeLoadingPayload
  "prompt:optimizeComplete": PromptOptimizeCompletePayload
  "prompt:optimizeAccept": PromptOptimizeAcceptPayload
  "prompt:optimizeReject": PromptOptimizeRejectPayload
  "prompt:optimizeDismiss": undefined
  "testMetric:open":
    | {
        focusedSimulationIds?: string[]
        /** Open the drawer straight to this conversation's transcript detail. */
        selectedSimulationId?: string
        /** Force a specific tab; pairs with selectedSimulationId for deep links. */
        viewMode?: "annotations" | "monitoring" | "simulations"
        requestedFilters?: {
          agreement?: "all" | "agree" | "disagree"
          includeUnreviewed?: boolean
          includeWithoutMetricOutput?: boolean
        }
      }
    | undefined
  "testMetric:run": undefined
  "testMetric:stop": undefined
  "testMetric:resultsUpdated": { storageKey: string }
}

type EventCallback<T extends MetricViewerEventType = MetricViewerEventType> = (
  data: MetricViewerEventPayloads[T],
) => void

type AnyEventCallback = (data?: unknown) => void

class EventBus {
  private events: Map<string, AnyEventCallback[]> = new Map()

  subscribe<T extends MetricViewerEventType>(
    event: T,
    callback: EventCallback<T>,
  ): () => void
  subscribe(event: string, callback: AnyEventCallback): () => void {
    if (!this.events.has(event)) {
      this.events.set(event, [])
    }
    this.events.get(event)!.push(callback)

    // Return unsubscribe function
    return () => {
      const callbacks = this.events.get(event)
      if (callbacks) {
        const index = callbacks.indexOf(callback)
        if (index > -1) {
          callbacks.splice(index, 1)
        }
      }
    }
  }

  emit<T extends MetricViewerEventType>(
    event: T,
    data?: MetricViewerEventPayloads[T],
  ): void
  emit(event: string, data?: unknown): void {
    const callbacks = this.events.get(event)
    if (callbacks) {
      callbacks.forEach((callback) => callback(data))
    }
  }

  clear(): void {
    this.events.clear()
  }
}

export const metricViewerEventBus = new EventBus()

// Event types
export const METRIC_VIEWER_EVENTS = {
  METRIC_UPDATED: "metric:updated",
  METRIC_SAVED: "metric:saved",
  METRIC_SAVE_ERROR: "metric:saveError",
  // Prompt optimization events
  PROMPT_OPTIMIZE_REQUEST: "prompt:optimizeRequest",
  PROMPT_OPTIMIZE_LOADING: "prompt:optimizeLoading",
  PROMPT_OPTIMIZE_COMPLETE: "prompt:optimizeComplete",
  PROMPT_OPTIMIZE_ACCEPT: "prompt:optimizeAccept",
  PROMPT_OPTIMIZE_REJECT: "prompt:optimizeReject",
  PROMPT_OPTIMIZE_DISMISS: "prompt:optimizeDismiss",
  // Test metric drawer events
  TEST_METRIC_OPEN: "testMetric:open",
  TEST_METRIC_RUN: "testMetric:run",
  TEST_METRIC_STOP: "testMetric:stop",
  TEST_METRIC_RESULTS_UPDATED: "testMetric:resultsUpdated",
} as const
